import React, { useEffect, useRef, useState } from 'react';
import "./journey.scss";

const Journey = () => {
  const sectionRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const rawProgress = (windowHeight - sectionTop) / (windowHeight + sectionHeight);
      setProgress(Math.max(0, Math.min(1, rawProgress)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ─── Desktop timings (2x2 grid Z-path) ───
  const desktopCardTimings = [
    { start: 0.15, end: 0.30 },
    { start: 0.30, end: 0.45 },
    { start: 0.50, end: 0.65 },
    { start: 0.65, end: 0.80 },
  ];
  const desktopConnectorTimings = [
    { start: 0.28, end: 0.32 },
    { start: 0.43, end: 0.52 },
    { start: 0.63, end: 0.67 },
  ];
  const desktopEntryPoints = ['top-left', 'left', 'top', 'right'];

  // ─── Mobile timings (vertical stack, one after another) ───
  const mobileCardTimings = [
    { start: 0.10, end: 0.25 },
    { start: 0.28, end: 0.43 },
    { start: 0.48, end: 0.63 },
    { start: 0.68, end: 0.83 },
  ];
  const mobileConnectorTimings = [
    { start: 0.24, end: 0.29 },
    { start: 0.42, end: 0.49 },
    { start: 0.62, end: 0.69 },
  ];
  const mobileEntryPoints = ['top-left', 'top', 'top', 'top'];

  // ─── Progress helpers ───
  const getProgress = (timings, index) => {
    const { start, end } = timings[index];
    if (progress <= start) return 0;
    if (progress >= end) return 1;
    return (progress - start) / (end - start);
  };

  const getGlowOpacity = (timings, index) => {
    const { start, end } = timings[index];
    if (progress < start) return 0;
    if (progress < start + 0.03) return ((progress - start) / 0.03) * 0.6;
    if (progress < end) return 0.6;
    const fadeEnd = end + 0.08;
    if (progress < fadeEnd) return 0.6 - ((progress - end) / 0.08) * 0.35;
    return 0.25;
  };

  // ─── SVG rounded rect path ───
  const getRoundedRectPath = (w, h, r, startFrom) => {
    const a = `A ${r},${r} 0 0 1`;
    const topEdge = `L ${w - r},1`;
    const trCorner = `${a} ${w},${r + 1}`;
    const rightEdge = `L ${w},${h - r}`;
    const brCorner = `${a} ${w - r},${h}`;
    const bottomEdge = `L ${r},${h}`;
    const blCorner = `${a} 1,${h - r}`;
    const leftEdge = `L 1,${r + 1}`;
    const tlCorner = `${a} ${r},1`;

    switch (startFrom) {
      case 'left':
        return `M 1,${h / 2} ${leftEdge} ${tlCorner} ${topEdge} ${trCorner} ${rightEdge} ${brCorner} ${bottomEdge} ${blCorner} L 1,${h / 2}`;
      case 'top':
        return `M ${w / 2},1 ${topEdge} ${trCorner} ${rightEdge} ${brCorner} ${bottomEdge} ${blCorner} ${leftEdge} ${tlCorner} L ${w / 2},1`;
      case 'right':
        return `M ${w},${h / 2} ${rightEdge} ${brCorner} ${bottomEdge} ${blCorner} ${leftEdge} ${tlCorner} ${topEdge} ${trCorner} L ${w},${h / 2}`;
      case 'top-left':
      default:
        return `M ${r},1 ${topEdge} ${trCorner} ${rightEdge} ${brCorner} ${bottomEdge} ${blCorner} ${leftEdge} ${tlCorner}`;
    }
  };

  // ─── Border Tracer SVG overlay ───
  const BorderTracer = ({ index, cardTimingsArr, entryPointsArr, idPrefix }) => {
    const drawProgress = getProgress(cardTimingsArr, index);
    const glowOpacity = getGlowOpacity(cardTimingsArr, index);
    const vw = 200;
    const vh = 90;
    const r = 10;
    const d = getRoundedRectPath(vw, vh, r, entryPointsArr[index]);
    const filterId = `${idPrefix}-glow-${index}`;

    return (
      <svg
        className="border-tracer-svg"
        viewBox={`0 0 ${vw + 1} ${vh + 1}`}
        preserveAspectRatio="none"
        style={{ opacity: glowOpacity }}
      >
        <defs>
          <filter id={filterId}>
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path d={d} fill="none" stroke="rgba(230, 255, 0, 0.06)" strokeWidth="1" />
        <path
          d={d}
          fill="none"
          stroke="rgba(230, 255, 0, 0.55)"
          strokeWidth="1.5"
          filter={`url(#${filterId})`}
          pathLength="1"
          strokeDasharray="1"
          strokeDashoffset={1 - drawProgress}
          style={{ transition: 'stroke-dashoffset 0.05s linear' }}
        />
      </svg>
    );
  };

  return (
    <div className='journew'>
      <section className='mainjourny' ref={sectionRef}>
        <div className='custom-container'>
          <div className='inner_journy'>
            <h2>The Builder Journey</h2>

            {/* ═══════════ DESKTOP LAYOUT ═══════════ */}
            <div className='desktop-journey'>
              {/* Top row */}
              <div className='yellowcards'>
                <div className='cardone newcard card-animated'>
                  <BorderTracer index={0} cardTimingsArr={desktopCardTimings} entryPointsArr={desktopEntryPoints} idPrefix="desk" />
                  <img src='/assets/onee.svg' alt="Register" />
                  <h3>Register as a builder</h3>
                  <h4>Get a Rain builder badge and access the Rain SDK.</h4>
                </div>

                <span className='connector-h'>
                  <span
                    className='connector-fill connector-fill-h'
                    style={{
                      transform: `scaleX(${getProgress(desktopConnectorTimings, 0)})`,
                      transformOrigin: 'left',
                    }}
                  />
                </span>

                <div className='cardone phonecard card-animated'>
                  <BorderTracer index={1} cardTimingsArr={desktopCardTimings} entryPointsArr={desktopEntryPoints} idPrefix="desk" />
                  <img src='/assets/twoo.svg' alt="Build" />
                  <h3>Build Your App</h3>
                  <h4>Architect your own prediction market platform.</h4>
                </div>
              </div>

              {/* Vertical connector (right side) */}
              <div className='vertical-connector-wrapper'>
                <span className='connector-v'>
                  <span
                    className='connector-fill connector-fill-v'
                    style={{
                      transform: `scaleY(${getProgress(desktopConnectorTimings, 1)})`,
                      transformOrigin: 'top',
                    }}
                  />
                </span>
              </div>

              {/* Bottom row: Scale (right, 3rd) ← connector ← Earn (left, 4th) */}
              <div className='lowcards'>
                <div className='cardone card-animated'>
                  <BorderTracer index={3} cardTimingsArr={desktopCardTimings} entryPointsArr={desktopEntryPoints} idPrefix="desk" />
                  <img src='/assets/threee.svg' alt="Earn" />
                  <h3>Earn Volume Share</h3>
                  <h4>Receive a guaranteed 0.5% share of all generated trading volume.</h4>
                </div>

                <span className='connector-h'>
                  <span
                    className='connector-fill connector-fill-h'
                    style={{
                      transform: `scaleX(${getProgress(desktopConnectorTimings, 2)})`,
                      transformOrigin: 'right',
                    }}
                  />
                </span>

                <div className='cardone card-animated'>
                  <BorderTracer index={2} cardTimingsArr={desktopCardTimings} entryPointsArr={desktopEntryPoints} idPrefix="desk" />
                  <img src='/assets/fourr.svg' alt="Scale" />
                  <h3>Scale with Grants</h3>
                  <h4>Apply for grants, marketing support, and exclusive rewards.</h4>
                </div>
              </div>
            </div>

            {/* ═══════════ MOBILE LAYOUT ═══════════ */}
            <div className='mobile-journey'>
              {/* Card 1 */}
              <div className='cardone newcard card-animated'>
                <BorderTracer index={0} cardTimingsArr={mobileCardTimings} entryPointsArr={mobileEntryPoints} idPrefix="mob" />
                <img src='/assets/onee.svg' alt="Register" />
                <h3>Register as a builder</h3>
                <h4>Get a Rain builder badge and access the Rain SDK.</h4>
              </div>

              {/* Vertical connector 1→2 */}
              <span className='mob-connector-v'>
                <span
                  className='connector-fill connector-fill-v'
                  style={{
                    transform: `scaleY(${getProgress(mobileConnectorTimings, 0)})`,
                    transformOrigin: 'top',
                  }}
                />
              </span>

              {/* Card 2 */}
              <div className='cardone phonecard card-animated'>
                <BorderTracer index={1} cardTimingsArr={mobileCardTimings} entryPointsArr={mobileEntryPoints} idPrefix="mob" />
                <img src='/assets/twoo.svg' alt="Build" />
                <h3>Build Your App</h3>
                <h4>Architect your own prediction market platform.</h4>
              </div>

              {/* Vertical connector 2→3 */}
              <span className='mob-connector-v'>
                <span
                  className='connector-fill connector-fill-v'
                  style={{
                    transform: `scaleY(${getProgress(mobileConnectorTimings, 1)})`,
                    transformOrigin: 'top',
                  }}
                />
              </span>

              {/* Card 3 */}
              <div className='cardone card-animated'>
                <BorderTracer index={2} cardTimingsArr={mobileCardTimings} entryPointsArr={mobileEntryPoints} idPrefix="mob" />
                <img src='/assets/threee.svg' alt="Earn" />
                <h3>Earn Volume Share</h3>
                <h4>Receive a guaranteed 0.5% share of all generated trading volume.</h4>
              </div>

              {/* Vertical connector 3→4 */}
              <span className='mob-connector-v'>
                <span
                  className='connector-fill connector-fill-v'
                  style={{
                    transform: `scaleY(${getProgress(mobileConnectorTimings, 2)})`,
                    transformOrigin: 'top',
                  }}
                />
              </span>

              {/* Card 4 */}
              <div className='cardone card-animated'>
                <BorderTracer index={3} cardTimingsArr={mobileCardTimings} entryPointsArr={mobileEntryPoints} idPrefix="mob" />
                <img src='/assets/fourr.svg' alt="Scale" />
                <h3>Scale with Grants</h3>
                <h4>Apply for grants, marketing support, and exclusive rewards.</h4>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Journey;
